// クリエイター詳細パネル（design/README.md §3）。投稿媒体・ジャンル・個別アフィリ料率・振込口座・参加案件。
// 口座番号は全桁を保存しない（下4桁のみ。plan.md §7-1 推奨B）。
import { useEffect, useState } from "react";
import type {
  GfCreatorPanelProps,
  GfIgFriend,
  GfInfluencerDetail,
  GfInvoiceStatus,
  GfPayee,
  GfPlatform,
  Loadable,
} from "../../../types/gifting";
import {
  addGfSns,
  createAffAffiliate,
  deleteGfSns,
  fetchGfInfluencer,
  linkGfIg,
  saveAffRate,
  saveGfPayee,
  updateGfInfluencer,
  updateGfSns,
} from "../../../api/giftingClient";
import {
  fmtFollowers,
  fmtPct,
  fmtYen,
  handleOf,
  linkedIgOf,
  PLATFORM_LABEL,
  primarySns,
  profileUrl,
  STAGE_NAMES,
  typeHasAffiliate,
} from "../../../lib/giftingRules";
import { GfAvatar, GfButton, GfErrorBanner, GfField, GfPanelFrame, GfSkeleton, GfTag } from "../ui/GfUi";
import { ConfirmModal } from "../tabs/kit/ConfirmModal";
import { affMapOf, errMsg, parseCount, payeeTag, stripAt } from "../tabs/kit/helpers";
import { IgPicker } from "../ui/IgPicker";


const PLATFORM_OPTIONS: GfPlatform[] = ["instagram", "youtube", "tiktok", "ameblo", "x", "threads", "other"];
const ACCOUNT_TYPE_LABEL = { ordinary: "普通", checking: "当座" } as const;
const INVOICE_LABEL: Record<GfInvoiceStatus, string> = { unsubmitted: "未提出", registered: "登録事業者（T番号）", exempt: "免税事業者" };
const DEFAULT_RATE = 0.1;

interface PayeeForm {
  bank: string;
  branch: string;
  type: "ordinary" | "checking";
  last4: string;
  kana: string;
  inv: GfInvoiceStatus;
  invNo: string;
}

function payeeFormOf(p: GfPayee | null): PayeeForm {
  return {
    bank: p?.bank_name ?? "",
    branch: p?.branch_name ?? "",
    type: p?.account_type ?? "ordinary",
    last4: p?.account_last4 ?? "",
    kana: p?.account_holder_kana ?? "",
    inv: p?.invoice_status ?? "unsubmitted",
    invNo: p?.invoice_number ?? "",
  };
}

function invoiceText(p: GfPayee | null): string {
  if (!p) return "未提出";
  if (p.invoice_status === "registered") return p.invoice_number || "登録事業者（番号未入力）";
  return INVOICE_LABEL[p.invoice_status];
}

export function CreatorPanel({ creatorId, data, nav, refresh, toast }: GfCreatorPanelProps) {
  const [detail, setDetail] = useState<Loadable<GfInfluencerDetail>>({ status: "loading" });
  const [reload, setReload] = useState(0);

  const [genre, setGenre] = useState("");
  const [genreBusy, setGenreBusy] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addPlatform, setAddPlatform] = useState<GfPlatform>("instagram");
  const [addHandle, setAddHandle] = useState("");
  const [addFollowers, setAddFollowers] = useState("");
  const [snsBusy, setSnsBusy] = useState(false);
  const [editing, setEditing] = useState<{ id: string; val: string } | null>(null);
  const [delSns, setDelSns] = useState<string | null>(null);

  const [igBusy, setIgBusy] = useState(false);

  const [rateDraft, setRateDraft] = useState("");
  const [rateBusy, setRateBusy] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [regBusy, setRegBusy] = useState(false);

  const [payeeEdit, setPayeeEdit] = useState(false);
  const [pf, setPf] = useState<PayeeForm>(payeeFormOf(null));
  const [payeeBusy, setPayeeBusy] = useState(false);
  const [payeeErr, setPayeeErr] = useState<string | null>(null);

  // 詳細取得（クリエイター切替・保存後の再取得）
  useEffect(() => {
    let live = true;
    setDetail((prev) => (prev.status === "ok" && prev.data.id === creatorId ? prev : { status: "loading" }));
    fetchGfInfluencer(creatorId)
      .then((d) => live && setDetail({ status: "ok", data: d }))
      .catch((e: unknown) => live && setDetail({ status: "error", message: errMsg(e) }));
    return () => {
      live = false;
    };
  }, [creatorId, reload]);

  // 別のクリエイターに切り替えたら編集状態をリセット
  useEffect(() => {
    setAddOpen(false);
    setEditing(null);
    setPayeeEdit(false);
    setPayeeErr(null);
  }, [creatorId]);

  const detailData = detail.status === "ok" ? detail.data : null;
  useEffect(() => {
    if (detailData) {
      setGenre(detailData.genre ?? "");
      if (!payeeEdit) setPf(payeeFormOf(detailData.payee));
    }
    // payeeEdit の切替では再初期化しない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailData]);

  const boardCreator = data.board.creators.find((c) => c.id === creatorId) ?? null;
  const name = detailData?.name ?? boardCreator?.name ?? "";
  const snsList = detailData?.sns ?? boardCreator?.sns ?? [];
  const main = primarySns(snsList);
  const genreShown = detailData?.genre ?? boardCreator?.genre ?? null;
  const linkedIg = linkedIgOf(snsList);
  // ig_follower_id → 紐付け先クリエイター（同じ相手を2人に紐付けない）
  const igLinkedOwner = new Map<number, string>();
  for (const c of data.board.creators) for (const s of c.sns) if (s.ig_follower_id != null) igLinkedOwner.set(s.ig_follower_id, c.id);

  const affs = affMapOf(data.affiliates);
  const aff = affs.get(creatorId) ?? null;
  useEffect(() => {
    setRateDraft(aff ? String(Math.round(aff.commission_rate * 1000) / 10) : "");
  }, [aff?.id, aff?.commission_rate]); // eslint-disable-line react-hooks/exhaustive-deps

  const after = async () => {
    setReload((r) => r + 1);
    await refresh();
  };

  // 参加中の案件の基本料率（アフィリ登録時の初期料率）
  const liveDeals = (detailData?.deals ?? []).filter((d) => d.stage < 8 && d.status !== "cancelled");
  const baseRate =
    liveDeals
      .map((d) => data.board.campaigns.find((c) => c.id === d.campaign_id))
      .find((c) => c && typeHasAffiliate(c.type) && c.base_rate !== null)?.base_rate ?? DEFAULT_RATE;

  // ---- 操作 ----
  const saveGenre = async () => {
    setGenreBusy(true);
    try {
      await updateGfInfluencer(creatorId, { genre: genre.trim() || null });
      toast("ジャンルを保存しました");
      await after();
    } catch (e) {
      toast(`保存できませんでした: ${errMsg(e)}`);
    } finally {
      setGenreBusy(false);
    }
  };

  const addSns = async () => {
    const h = stripAt(addHandle);
    if (!h) return toast("ハンドルを入力してください");
    const f = parseCount(addFollowers);
    if (f !== null && Number.isNaN(f)) return toast("フォロワー数は数字（例: 12400 / 1.2万）で入力してください");
    setSnsBusy(true);
    try {
      await addGfSns(creatorId, { platform: addPlatform, handle: h, follower_count: f, is_primary: snsList.length === 0 ? 1 : 0 });
      toast("媒体を追加しました");
      setAddHandle("");
      setAddFollowers("");
      setAddOpen(false);
      await after();
    } catch (e) {
      toast(`追加できませんでした: ${errMsg(e)}`);
    } finally {
      setSnsBusy(false);
    }
  };

  const saveFollowers = async () => {
    if (!editing) return;
    const f = parseCount(editing.val);
    if (f !== null && Number.isNaN(f)) return toast("フォロワー数は数字で入力してください");
    setSnsBusy(true);
    try {
      await updateGfSns(creatorId, editing.id, { follower_count: f });
      setEditing(null);
      await after();
    } catch (e) {
      toast(`保存できませんでした: ${errMsg(e)}`);
    } finally {
      setSnsBusy(false);
    }
  };

  const removeSns = async () => {
    if (!delSns) return;
    setSnsBusy(true);
    try {
      await deleteGfSns(creatorId, delSns);
      toast("媒体を削除しました");
      setDelSns(null);
      await after();
    } catch (e) {
      toast(`削除できませんでした: ${errMsg(e)}`);
    } finally {
      setSnsBusy(false);
    }
  };

  const linkIg = async (f: GfIgFriend) => {
    const owner = igLinkedOwner.get(f.id);
    if (owner && owner !== creatorId) {
      const ownerName = data.board.creators.find((c) => c.id === owner)?.name ?? "別のクリエイター";
      return toast(`@${f.username ?? f.id} は既に「${ownerName}」に紐付いています`);
    }
    setIgBusy(true);
    try {
      const r = await linkGfIg(creatorId, f.id);
      toast(`Instagram（@${r.username ?? f.username ?? f.id}）と連携しました`);
      await after();
    } catch (e) {
      toast(`連携できませんでした: ${errMsg(e)}`);
    } finally {
      setIgBusy(false);
    }
  };

  const saveRate = async () => {
    if (!aff) return;
    const v = Number(rateDraft);
    if (rateDraft.trim() === "" || !Number.isFinite(v) || v < 0 || v > 100) return toast("料率は 0〜100 の数字（%）で入力してください");
    setRateBusy(true);
    try {
      await saveAffRate(aff.id, Math.round(v * 10) / 1000);
      toast("個別料率を保存しました");
      await after();
    } catch (e) {
      toast(`保存できませんでした: ${errMsg(e)}`);
    } finally {
      setRateBusy(false);
    }
  };

  const registerAff = async () => {
    if (!detailData) return;
    setRegBusy(true);
    try {
      const res = await createAffAffiliate({
        name: detailData.name,
        type: "influencer",
        email: detailData.email,
        gifting_influencer_id: creatorId,
        commission_rate: baseRate,
      });
      toast(`アフィリエイトを登録しました（クーポン ${res.coupon_code}）`);
      setRegOpen(false);
      await after();
    } catch (e) {
      toast(`登録できませんでした: ${errMsg(e)}`);
    } finally {
      setRegBusy(false);
    }
  };

  const savePayee = async () => {
    const last4 = pf.last4.trim();
    if (last4 && !/^\d{4}$/.test(last4)) return setPayeeErr("口座番号は下4桁の数字のみ入力してください");
    const invNo = pf.invNo.trim().toUpperCase();
    if (pf.inv === "registered" && !/^T\d{13}$/.test(invNo)) return setPayeeErr("インボイス番号は T + 13桁の数字で入力してください");
    setPayeeBusy(true);
    setPayeeErr(null);
    try {
      await saveGfPayee(creatorId, {
        bank_name: pf.bank.trim() || null,
        branch_name: pf.branch.trim() || null,
        account_type: pf.type,
        account_last4: last4 || null,
        account_holder_kana: pf.kana.trim() || null,
        invoice_status: pf.inv,
        invoice_number: pf.inv === "registered" ? invNo : null,
      });
      toast("振込口座を保存しました");
      setPayeeEdit(false);
      await after();
    } catch (e) {
      setPayeeErr(errMsg(e));
    } finally {
      setPayeeBusy(false);
    }
  };

  // ---- 表示用 ----
  const payee = detailData?.payee ?? null;
  const payeeStatus = payee?.status ?? boardCreator?.payee_status ?? "unregistered";
  const ptag = payeeTag(payeeStatus);
  const monthlyRow =
    data.monthly.status === "ok" ? data.monthly.data.by_affiliate.find((a) => a.gifting_influencer_id === creatorId) ?? null : null;
  const salesText =
    data.monthly.status === "loading" ? "…" : data.monthly.status === "error" ? "—" : aff ? fmtYen(monthlyRow?.sales ?? 0) : "—";
  // Amazon 専用コード（追跡ID）経由。affiliate 側で追跡IDが登録された人だけ出す（2026-11-02）
  const amazonRow = monthlyRow?.amazon ?? null;
  const amazonTracking = monthlyRow?.amazon_tracking_id ?? aff?.amazon_tracking_id ?? null;
  const amazonText =
    data.monthly.status === "loading" ? "…" : data.monthly.status === "error" ? "—" : amazonTracking ? `${fmtYen(amazonRow?.sales ?? 0)}（${amazonRow?.conversions ?? 0}件）` : null;

  return (
    <>
    <GfPanelFrame onClose={nav.closePanel} label="クリエイター詳細">
      <div className="gf-pc-head">
        <GfAvatar name={name || "?"} size={44} />
        <div className="gf-pc-head-text">
          <span className="gf-pc-name">{name || "…"}</span>
          <span className="gf-note">
            {[handleOf(main), genreShown].filter(Boolean).join("・") || "—"}
          </span>
        </div>
      </div>

      {detail.status === "error" && <GfErrorBanner message={`詳細を取得できませんでした（${detail.message}）`} onRetry={() => setReload((r) => r + 1)} />}
      {detail.status === "loading" && (
        <div className="gf-panel-section">
          <GfSkeleton height={44} />
          <GfSkeleton height={44} />
          <GfSkeleton height={90} />
        </div>
      )}

      {detailData && (
        <>
          {/* 投稿媒体 */}
          <div className="gf-panel-section">
            <div className="gf-panel-section-head">
              <span className="gf-panel-section-title">投稿媒体</span>
              <button type="button" className="gf-link-btn" onClick={() => setAddOpen((v) => !v)}>
                {addOpen ? "閉じる" : "＋ 追加"}
              </button>
            </div>
            {detailData.sns.length === 0 && <div className="gf-note">媒体が登録されていません</div>}
            {detailData.sns.map((s) => {
              const url = profileUrl(s.platform, s.handle, s.url);
              const isEditing = editing?.id === s.id;
              return (
                <div className="gf-sns-row" key={s.id}>
                  <span className="gf-sns-name">{PLATFORM_LABEL[s.platform]}</span>
                  {url ? (
                    <a className="gf-sns-url" href={url} target="_blank" rel="noopener noreferrer">
                      {url.replace(/^https?:\/\/(www\.)?/, "")}
                    </a>
                  ) : (
                    <span className="gf-sns-url">{handleOf(s)}</span>
                  )}
                  {isEditing ? (
                    <span className="gf-sns-edit">
                      <input
                        className="gf-input is-mono gf-input-sm"
                        aria-label="フォロワー数"
                        value={editing.val}
                        autoFocus
                        inputMode="decimal"
                        onChange={(e) => setEditing({ id: s.id, val: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void saveFollowers();
                          if (e.key === "Escape") setEditing(null);
                        }}
                      />
                      <button type="button" className="gf-link-btn" disabled={snsBusy} onClick={() => void saveFollowers()}>
                        保存
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="gf-sns-val"
                      title="フォロワー数を編集"
                      onClick={() => setEditing({ id: s.id, val: s.follower_count === null ? "" : String(s.follower_count) })}
                    >
                      {fmtFollowers(s.follower_count)}
                    </button>
                  )}
                  <button type="button" className="gf-icon-btn" aria-label={`${PLATFORM_LABEL[s.platform]}を削除`} onClick={() => setDelSns(s.id)}>
                    ×
                  </button>
                </div>
              );
            })}
            {addOpen && (
              <div className="gf-sns-add">
                <select className="gf-select" aria-label="媒体" value={addPlatform} onChange={(e) => setAddPlatform(e.target.value as GfPlatform)}>
                  {PLATFORM_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {PLATFORM_LABEL[p]}
                    </option>
                  ))}
                </select>
                <input className="gf-input" aria-label="ハンドル" placeholder="ハンドル（@なし）" value={addHandle} onChange={(e) => setAddHandle(e.target.value)} />
                <input
                  className="gf-input is-mono"
                  aria-label="フォロワー数"
                  placeholder="フォロワー数"
                  inputMode="decimal"
                  value={addFollowers}
                  onChange={(e) => setAddFollowers(e.target.value)}
                />
                <GfButton variant="primary" size="sm" disabled={snsBusy} onClick={() => void addSns()}>
                  追加
                </GfButton>
              </div>
            )}
          </div>

          {/* Instagram 連携（ig-harness のフォロワーに紐付け → エントリーからDM履歴・送信） */}
          <div className="gf-panel-section">
            <span className="gf-panel-section-title">Instagram連携</span>
            {linkedIg ? (
              <div className="gf-ig-ok">
                <GfTag tone="ok">連携済み</GfTag>
                <span className="gf-mono">@{linkedIg.handle}</span>
                <span className="gf-note">エントリー詳細の「DM」から履歴の確認・送信ができます。</span>
              </div>
            ) : (
              <>
                <div className="gf-note">DMでやり取りした相手を検索して紐付けると、エントリー詳細からDMの履歴確認・送信ができます。</div>
                <IgPicker pickLabel="紐付ける" disabled={igBusy} linkedIds={igLinkedOwner} onPick={(f) => void linkIg(f)} />
              </>
            )}
          </div>

          {/* ジャンル */}
          <div className="gf-panel-section">
            <GfField label="ジャンル">
              <div className="gf-inline-row">
                <input className="gf-input" value={genre} onChange={(e) => setGenre(e.target.value)} placeholder="例）暮らし・料理" maxLength={50} />
                <GfButton size="sm" disabled={genreBusy || genre.trim() === (detailData.genre ?? "")} onClick={() => void saveGenre()}>
                  保存
                </GfButton>
              </div>
            </GfField>
          </div>

          {/* アフィリ */}
          <div className="gf-boxed gf-boxed-col">
            <div className="gf-box-row">
              <span className="gf-muted">個別アフィリ料率</span>
              {data.affiliates.status === "loading" ? (
                <span className="gf-mono">…</span>
              ) : data.affiliates.status === "error" ? (
                <span className="gf-faint">取得できませんでした</span>
              ) : aff ? (
                <span className="gf-inline-row is-tight">
                  <span className="gf-input-affix is-sm gf-affix-rate">
                    <input
                      aria-label="個別アフィリ料率（%）"
                      inputMode="decimal"
                      value={rateDraft}
                      onChange={(e) => setRateDraft(e.target.value.replace(/[^0-9.]/g, ""))}
                      onKeyDown={(e) => e.key === "Enter" && void saveRate()}
                    />
                    <span className="gf-muted">%</span>
                  </span>
                  <GfButton
                    size="sm"
                    disabled={rateBusy || rateDraft === String(Math.round(aff.commission_rate * 1000) / 10)}
                    onClick={() => void saveRate()}
                  >
                    保存
                  </GfButton>
                </span>
              ) : (
                <GfTag tone="caution">未登録</GfTag>
              )}
            </div>
            {data.affiliates.status === "ok" && !aff && (
              <div className="gf-aff-register">
                <GfButton size="sm" onClick={() => setRegOpen(true)}>
                  アフィリ登録（Shopifyクーポン発行）
                </GfButton>
                <span className="gf-note">登録するとクリエイター専用のクーポンが発行され、売上が計測されます。</span>
              </div>
            )}
            {aff?.coupon_code && (
              <div className="gf-box-row">
                <span className="gf-muted">クーポン</span>
                <span className="gf-mono">{aff.coupon_code}</span>
              </div>
            )}
            <div className="gf-box-row">
              <span className="gf-muted">アフィリ売上（今月・Shopify）</span>
              <span className="gf-mono">{salesText}</span>
            </div>
            {amazonText && (
              <div className="gf-box-row">
                <span className="gf-muted">Amazon コード経由（今月）</span>
                <span className="gf-mono">{amazonText}</span>
              </div>
            )}
            <div className="gf-note">
              「今月」の売上です（累計は未集計）。
              {amazonText
                ? ` Amazon は専用コード（${monthlyRow?.amazon_coupon_code ?? aff?.amazon_coupon_code ?? "追跡ID " + amazonTracking}）経由の注文を毎朝取り込み${
                    (monthlyRow?.amazon_commission_rate ?? aff?.amazon_commission_rate) == null ? "・報酬対象外（表示のみ）" : ""
                  }。`
                : ""}
            </div>
          </div>

          {/* 振込口座 */}
          <div className="gf-panel-section">
            <div className="gf-panel-section-head">
              <span className="gf-panel-section-title">振込口座</span>
              {!payeeEdit && (
                <button
                  type="button"
                  className="gf-link-btn"
                  onClick={() => {
                    setPf(payeeFormOf(payee));
                    setPayeeErr(null);
                    setPayeeEdit(true);
                  }}
                >
                  {payeeStatus === "unregistered" && !payee?.bank_name ? "口座を登録" : "編集"}
                </button>
              )}
            </div>
            {!payeeEdit ? (
              <div className="gf-payee-card">
                <div className="gf-box-row">
                  <span>{payee?.bank_name ? `${payee.bank_name}${payee.branch_name ? ` ${payee.branch_name}` : ""}` : "—"}</span>
                  <GfTag tone={ptag.tone}>{ptag.label}</GfTag>
                </div>
                <span className="gf-mono gf-muted gf-small">
                  {payee?.account_last4 ? `${payee.account_type ? ACCOUNT_TYPE_LABEL[payee.account_type] : ""} ****${payee.account_last4}` : "—"}
                </span>
                {payee?.account_holder_kana && <span className="gf-note">名義（カナ）：{payee.account_holder_kana}</span>}
                <span className="gf-note">インボイス：{invoiceText(payee)}</span>
              </div>
            ) : (
              <div className="gf-payee-form">
                {payeeErr && <GfErrorBanner message={payeeErr} />}
                <div className="gf-form-grid">
                  <GfField label="銀行">
                    <input className="gf-input" value={pf.bank} onChange={(e) => setPf({ ...pf, bank: e.target.value })} maxLength={50} />
                  </GfField>
                  <GfField label="支店">
                    <input className="gf-input" value={pf.branch} onChange={(e) => setPf({ ...pf, branch: e.target.value })} maxLength={50} />
                  </GfField>
                  <GfField label="種別">
                    <select className="gf-select" value={pf.type} onChange={(e) => setPf({ ...pf, type: e.target.value as PayeeForm["type"] })}>
                      <option value="ordinary">普通</option>
                      <option value="checking">当座</option>
                    </select>
                  </GfField>
                  <GfField label="口座番号 下4桁" hint="全桁は保存しません">
                    <input
                      className="gf-input is-mono"
                      value={pf.last4}
                      inputMode="numeric"
                      maxLength={4}
                      placeholder="0000"
                      onChange={(e) => setPf({ ...pf, last4: e.target.value.replace(/\D/g, "") })}
                    />
                  </GfField>
                </div>
                <GfField label="口座名義（カナ）">
                  <input className="gf-input" value={pf.kana} onChange={(e) => setPf({ ...pf, kana: e.target.value })} placeholder="ヤマダ ハナコ" maxLength={60} />
                </GfField>
                <GfField label="インボイス">
                  <select className="gf-select" value={pf.inv} onChange={(e) => setPf({ ...pf, inv: e.target.value as GfInvoiceStatus })}>
                    {(Object.keys(INVOICE_LABEL) as GfInvoiceStatus[]).map((k) => (
                      <option key={k} value={k}>
                        {INVOICE_LABEL[k]}
                      </option>
                    ))}
                  </select>
                </GfField>
                {pf.inv === "registered" && (
                  <GfField label="インボイス番号（T + 13桁）">
                    <input
                      className="gf-input is-mono"
                      value={pf.invNo}
                      maxLength={14}
                      placeholder="T0000000000000"
                      onChange={(e) => setPf({ ...pf, invNo: e.target.value })}
                    />
                  </GfField>
                )}
                <div className="gf-note">口座番号の全桁はここに保存しません（下4桁のみ）。全桁は従来どおり受領して銀行側で登録してください。</div>
                <div className="gf-inline-row">
                  <GfButton onClick={() => setPayeeEdit(false)} disabled={payeeBusy}>
                    キャンセル
                  </GfButton>
                  <GfButton variant="primary" onClick={() => void savePayee()} disabled={payeeBusy}>
                    {payeeBusy ? "保存中…" : "保存"}
                  </GfButton>
                </div>
              </div>
            )}
          </div>

          {/* 参加案件 */}
          <div className="gf-panel-section">
            <span className="gf-panel-section-title">参加案件</span>
            {detailData.deals.length === 0 && <div className="gf-note">参加している案件はありません</div>}
            {[...detailData.deals]
              .sort((a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? ""))
              .map((d) => {
                const camp = d.campaign_id ? data.board.campaigns.find((c) => c.id === d.campaign_id) : null;
                return (
                  <button type="button" className="gf-deal-link" key={d.id} onClick={() => nav.openEntry(d.id, "deals")}>
                    <span>{camp?.name ?? d.product_name}</span>
                    <span className="gf-deal-link-stage">{d.status === "cancelled" ? "中止" : STAGE_NAMES[d.stage]}</span>
                  </button>
                );
              })}
          </div>
        </>
      )}

    </GfPanelFrame>
      <ConfirmModal
        open={regOpen}
        title="アフィリエイトを登録"
        confirmLabel="登録する"
        busy={regBusy}
        onConfirm={() => void registerAff()}
        onClose={() => setRegOpen(false)}
      >
        <p>
          {name} のアフィリエイト登録を行い、Shopify のクーポンを発行します。料率は {fmtPct(baseRate)} で作成します（登録後に変更できます）。
        </p>
        <p className="gf-note">発行したクーポンは取り消せません。よろしいですか。</p>
      </ConfirmModal>
      <ConfirmModal
        open={delSns !== null}
        title="媒体を削除"
        confirmLabel="削除する"
        danger
        busy={snsBusy}
        onConfirm={() => void removeSns()}
        onClose={() => setDelSns(null)}
      >
        <p>この投稿媒体を削除しますか。</p>
      </ConfirmModal>
    </>
  );
}
